import { ApolloError } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import { ReactNode, useState } from 'react';
import SupportLink from 'src/components/intercom/SupportLink';
import { useRequestResetPasswordMutation } from 'src/generated/graphql';

interface ForgotPasswordVariables {
  email: string;
}

export default function useForgotPassword(onSuccess: (email: string) => void) {
  const [forgotPassword] = useRequestResetPasswordMutation();
  const [submissionError, setSubmissionError] = useState<ReactNode | string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = (variables: ForgotPasswordVariables) => {
    setSubmissionError(null);
    setIsSubmitting(true);
    return forgotPassword({ variables })
      .then(() => onSuccess(variables.email))
      .catch((err: ApolloError) => {
        setIsSubmitting(false);
        const cannotFindEmailMessage = 'We cannot find a user registered with that email address.';
        const message = err.message.includes(cannotFindEmailMessage)
          ? cannotFindEmailMessage
          : err.message; // shorten the error message when the email is not found
        setSubmissionError(
          <Text fontSize="inherit" color="inherit">
            Sorry, we couldn&apos;t attempt a password reset. Please{' '}
            <SupportLink
              message={`Help! I can't reset my password. I get an error message "${message}"`}
              color="inherit"
            >
              contact support
            </SupportLink>
            . {message}
          </Text>,
        );
      });
  };

  return { submit, submissionError, isSubmitting };
}
